/**
 * MIA AI Store
 * Centralized state management for MIA AI features
 * Uses Vue 3 reactive state (similar to Pinia pattern)
 */

import { reactive, readonly, computed } from "vue";
import { apiGet, apiPost, apiPut, apiGetPaginated } from "../services/api";
import { miaUploadService } from "../services/uploadService";
import { miaRefreshService } from "../services/refreshService";
import {
  MiaFeatureType,
  MiaEndpoints,
  OperationStatus,
  MiaRefreshType,
} from "../types";

/**
 * MIA Store State
 */
const state = reactive({
  // Recent operations
  operations: [],
  operationsLoading: false,
  operationsError: null,

  // Problem Solving
  problems: [],
  problemsLoading: false,
  problemsError: null,
  problemsMeta: null,

  // Flash Cards
  flashCardDecks: [],
  flashCardDecksLoading: false,
  flashCardDecksError: null,
  flashCardDecksMeta: null,

  // Question Banks
  questionBanks: [],
  questionBanksLoading: false,
  questionBanksError: null,
  questionBanksMeta: null,

  // Summaries
  summaries: [],
  summariesLoading: false,
  summariesError: null,
  summariesMeta: null,

  // Current operation details
  currentProblem: null,
  currentDeck: null,
  currentBank: null,
  currentSummary: null,

  // Form state
  formData: {
    inputType: null,
    textContent: null,
    selectedFile: null,
    language: "auto",
    selectedFeature: null,
    featureData: {},
  },

  // Guest mode
  isGuest: false,
});

/**
 * Computed getters
 */
const getters = {
  hasInput: computed(() => {
    const { inputType, textContent, selectedFile } = state.formData;
    return (
      (inputType === "text" && textContent) ||
      (inputType !== "text" && selectedFile)
    );
  }),

  canSubmit: computed(() => {
    return getters.hasInput.value && state.formData.selectedFeature !== null;
  }),

  pendingOperations: computed(() => {
    return state.operations.filter(
      (op) =>
        op.status === OperationStatus.PENDING ||
        op.status === OperationStatus.PROCESSING
    );
  }),

  completedOperations: computed(() => {
    return state.operations.filter(
      (op) => op.status === OperationStatus.COMPLETED
    );
  }),

  failedOperations: computed(() => {
    return state.operations.filter(
      (op) => op.status === OperationStatus.FAILED
    );
  }),
};

/**
 * Actions
 */
const actions = {
  // ==================== Operations ====================

  /**
   * Fetch recent operations
   */
  async fetchOperations() {
    state.operationsLoading = true;
    state.operationsError = null;

    try {
      const result = await apiGet(MiaEndpoints.OPERATIONS);

      if (result.error) {
        state.operationsError = result.error.message;
        return false;
      }

      state.operations = Array.isArray(result.data) ? result.data : [];
      return true;
    } catch (e) {
      state.operationsError = e.message || "Failed to fetch operations";
      return false;
    } finally {
      state.operationsLoading = false;
    }
  },

  // ==================== Problems ====================

  /**
   * Fetch problems list
   */
  async fetchProblems(page = 1, perPage = 15) {
    state.problemsLoading = true;
    state.problemsError = null;

    try {
      const result = await apiGetPaginated(
        MiaEndpoints.PROBLEMS,
        page,
        perPage
      );

      if (result.error) {
        state.problemsError = result.error.message;
        return false;
      }

      if (page === 1) {
        state.problems = result.data;
      } else {
        state.problems = [...state.problems, ...result.data];
      }
      state.problemsMeta = result.meta;
      return true;
    } catch (e) {
      state.problemsError = e.message || "Failed to fetch problems";
      return false;
    } finally {
      state.problemsLoading = false;
    }
  },

  /**
   * Create a new problem (text input)
   */
  async createProblem(content, solutionType, language = "auto") {
    try {
      const result = await apiPost(MiaEndpoints.PROBLEMS, {
        input_type: "text",
        content: content.trim(),
        solution_type: solutionType,
        language,
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      miaRefreshService.refresh(MiaRefreshType.PROBLEM_SOLVING);

      return {
        success: true,
        data: result.data,
        problemId: result.data.problem_id || result.data.problemId,
        solutionId: result.data.solution_id || result.data.solutionId,
      };
    } catch (e) {
      return { success: false, error: e.message || "Failed to create problem" };
    }
  },

  /**
   * Update problem title
   */
  async updateProblemTitle(problemId, title) {
    try {
      const result = await apiPut(`${MiaEndpoints.PROBLEMS}/${problemId}`, {
        title,
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      // Update local state
      const index = state.problems.findIndex((p) => p.id === problemId);
      if (index > -1) {
        state.problems[index] = { ...state.problems[index], title };
      }

      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || "Failed to update title" };
    }
  },

  /**
   * Get problem solution status
   */
  async getProblemStatus(problemId, solutionId) {
    try {
      const endpoint = MiaEndpoints.PROBLEM_STATUS(problemId, solutionId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get status" };
    }
  },

  /**
   * Retry failed problem solution
   */
  async retryProblem(problemId, solutionId) {
    try {
      const endpoint = MiaEndpoints.PROBLEM_RETRY(problemId, solutionId);
      const result = await apiPost(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to retry" };
    }
  },

  // ==================== Flash Cards ====================

  /**
   * Fetch flash card decks
   */
  async fetchFlashCardDecks(page = 1, perPage = 15) {
    state.flashCardDecksLoading = true;
    state.flashCardDecksError = null;

    try {
      const result = await apiGetPaginated(
        MiaEndpoints.FLASH_CARD_DECKS,
        page,
        perPage
      );

      if (result.error) {
        state.flashCardDecksError = result.error.message;
        return false;
      }

      if (page === 1) {
        state.flashCardDecks = result.data;
      } else {
        state.flashCardDecks = [...state.flashCardDecks, ...result.data];
      }
      state.flashCardDecksMeta = result.meta;
      return true;
    } catch (e) {
      state.flashCardDecksError = e.message || "Failed to fetch decks";
      return false;
    } finally {
      state.flashCardDecksLoading = false;
    }
  },

  /**
   * Create a new flash card deck (text input)
   */
  async createFlashCardDeck(
    content,
    cardCount,
    language = "auto",
    description = ""
  ) {
    try {
      const result = await apiPost(MiaEndpoints.FLASH_CARD_DECKS, {
        source_type: "text",
        content: content.trim(),
        card_count: cardCount,
        language,
        ...(description && { description }),
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      miaRefreshService.refresh(MiaRefreshType.FLASH_CARDS);

      return {
        success: true,
        data: result.data,
        deckId: result.data.deck_id || result.data.deckId,
      };
    } catch (e) {
      return { success: false, error: e.message || "Failed to create deck" };
    }
  },

  /**
   * Update deck title
   */
  async updateDeckTitle(deckId, title) {
    try {
      const result = await apiPut(
        `${MiaEndpoints.FLASH_CARD_DECKS}/${deckId}`,
        {
          title,
        }
      );

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      // Update local state
      const index = state.flashCardDecks.findIndex((d) => d.id === deckId);
      if (index > -1) {
        state.flashCardDecks[index] = { ...state.flashCardDecks[index], title };
      }

      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || "Failed to update title" };
    }
  },

  /**
   * Get deck status
   */
  async getDeckStatus(deckId) {
    try {
      const endpoint = MiaEndpoints.FLASH_CARD_DECK_STATUS(deckId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get status" };
    }
  },

  /**
   * Get deck statistics
   */
  async getDeckStatistics(deckId) {
    try {
      const endpoint = MiaEndpoints.FLASH_CARD_DECK_STATISTICS(deckId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get statistics" };
    }
  },

  /**
   * Get study cards
   */
  async getStudyCards(deckId) {
    try {
      const endpoint = MiaEndpoints.FLASH_CARD_STUDY(deckId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get cards" };
    }
  },

  /**
   * Update card progress
   */
  async updateCardProgress(cardId, isCorrect) {
    try {
      const endpoint = MiaEndpoints.FLASH_CARD_PROGRESS(cardId);
      const result = await apiPost(endpoint, { is_correct: isCorrect });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return {
        success: false,
        error: e.message || "Failed to update progress",
      };
    }
  },

  /**
   * Retry failed deck generation
   */
  async retryDeck(deckId) {
    try {
      const endpoint = MiaEndpoints.FLASH_CARD_DECK_RETRY(deckId);
      const result = await apiPost(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to retry" };
    }
  },

  // ==================== Question Banks ====================

  /**
   * Fetch question banks
   */
  async fetchQuestionBanks(page = 1, perPage = 15) {
    state.questionBanksLoading = true;
    state.questionBanksError = null;

    try {
      const result = await apiGetPaginated(
        MiaEndpoints.QUESTION_BANKS,
        page,
        perPage,
        { ai_generated: 1 }
      );

      if (result.error) {
        state.questionBanksError = result.error.message;
        return false;
      }

      if (page === 1) {
        state.questionBanks = result.data;
      } else {
        state.questionBanks = [...state.questionBanks, ...result.data];
      }
      state.questionBanksMeta = result.meta;
      return true;
    } catch (e) {
      state.questionBanksError = e.message || "Failed to fetch question banks";
      return false;
    } finally {
      state.questionBanksLoading = false;
    }
  },

  /**
   * Create a new question bank (text input)
   */
  async createQuestionBank(content, questionCount, language = "auto") {
    try {
      const result = await apiPost(MiaEndpoints.QUESTION_BANKS_FROM_CONTENT, {
        content: content.trim(),
        question_count: questionCount,
        language,
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      miaRefreshService.refresh(MiaRefreshType.QBANK);

      return {
        success: true,
        data: result.data,
        bankId: result.data.bank_id || result.data.bankId,
      };
    } catch (e) {
      return {
        success: false,
        error: e.message || "Failed to create question bank",
      };
    }
  },

  /**
   * Update question bank title
   */
  async updateQuestionBankTitle(bankId, title) {
    try {
      const result = await apiPut(`api/question-banks/${bankId}`, { title });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      // Update local state
      const index = state.questionBanks.findIndex((b) => b.id === bankId);
      if (index > -1) {
        state.questionBanks[index] = {
          ...state.questionBanks[index],
          name: title,
        };
      }

      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || "Failed to update title" };
    }
  },

  /**
   * Get question bank status
   */
  async getQuestionBankStatus(bankId) {
    try {
      const endpoint = MiaEndpoints.QUESTION_BANK_STATUS(bankId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get status" };
    }
  },

  /**
   * Retry failed question bank generation
   */
  async retryQuestionBank(bankId) {
    try {
      const endpoint = MiaEndpoints.QUESTION_BANK_RETRY(bankId);
      const result = await apiPost(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to retry" };
    }
  },

  // ==================== Summaries ====================

  /**
   * Fetch summaries
   */
  async fetchSummaries(page = 1, perPage = 15) {
    state.summariesLoading = true;
    state.summariesError = null;

    try {
      const result = await apiGetPaginated(
        MiaEndpoints.SUMMARIES,
        page,
        perPage
      );

      if (result.error) {
        state.summariesError = result.error.message;
        return false;
      }

      if (page === 1) {
        state.summaries = result.data;
      } else {
        state.summaries = [...state.summaries, ...result.data];
      }
      state.summariesMeta = result.meta;
      return true;
    } catch (e) {
      state.summariesError = e.message || "Failed to fetch summaries";
      return false;
    } finally {
      state.summariesLoading = false;
    }
  },

  /**
   * Create a new summary (text input)
   */
  async createSummary(content, outputType, language = "auto", options = {}) {
    try {
      const result = await apiPost(MiaEndpoints.SUMMARY_PROCESS, {
        text: content.trim(),
        output_type: outputType,
        language,
        ...(Object.keys(options).length > 0 && { options }),
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      miaRefreshService.refresh(MiaRefreshType.SUMMARY);

      return {
        success: true,
        data: result.data,
        uploadId: result.data.upload_id || result.data.uploadId,
      };
    } catch (e) {
      return { success: false, error: e.message || "Failed to create summary" };
    }
  },

  /**
   * Update summary title
   */
  async updateSummaryTitle(summaryId, title) {
    try {
      const result = await apiPut(`${MiaEndpoints.SUMMARIES}/${summaryId}`, {
        title,
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      // Update local state
      const index = state.summaries.findIndex((s) => s.id === summaryId);
      if (index > -1) {
        state.summaries[index] = { ...state.summaries[index], title };
      }

      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || "Failed to update title" };
    }
  },

  /**
   * Get summary status
   */
  async getSummaryStatus(summaryId) {
    try {
      const endpoint = MiaEndpoints.SUMMARY_STATUS(summaryId);
      const result = await apiGet(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to get status" };
    }
  },

  /**
   * Retry failed summary generation
   */
  async retrySummary(summaryId) {
    try {
      const endpoint = MiaEndpoints.SUMMARY_RETRY(summaryId);
      const result = await apiPost(endpoint);

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true, data: result.data };
    } catch (e) {
      return { success: false, error: e.message || "Failed to retry" };
    }
  },

  // ==================== Form Actions ====================

  /**
   * Set form input type
   */
  setInputType(type) {
    state.formData.inputType = type;
    state.formData.selectedFile = null;
    state.formData.textContent = null;
  },

  /**
   * Set form text content
   */
  setTextContent(content) {
    state.formData.textContent = content;
  },

  /**
   * Set form selected file
   */
  setSelectedFile(file) {
    state.formData.selectedFile = file;
  },

  /**
   * Set form language
   */
  setLanguage(language) {
    state.formData.language = language;
  },

  /**
   * Set form selected feature
   */
  setSelectedFeature(feature) {
    state.formData.selectedFeature = feature;
    state.formData.featureData = {};
  },

  /**
   * Set form feature data
   */
  setFeatureData(data) {
    state.formData.featureData = { ...state.formData.featureData, ...data };
  },

  /**
   * Reset form data
   */
  resetFormData() {
    state.formData = {
      inputType: null,
      textContent: null,
      selectedFile: null,
      language: "auto",
      selectedFeature: null,
      featureData: {},
    };
  },

  /**
   * Reset feature data only
   */
  resetFeatureData() {
    state.formData.featureData = {};
  },

  /**
   * Clear input (keep input type if hasCurrentInput)
   */
  clearInput(hasCurrentInput = true) {
    if (hasCurrentInput) {
      state.formData.textContent = null;
      state.formData.selectedFile = null;
      state.formData.language = "auto";
    } else {
      state.formData.inputType = null;
      state.formData.textContent = null;
      state.formData.selectedFile = null;
      state.formData.language = "auto";
      state.formData.selectedFeature = null;
      state.formData.featureData = {};
    }
  },

  /**
   * Set guest mode
   */
  setGuestMode(isGuest) {
    state.isGuest = isGuest;
  },
};

/**
 * MIA Store - Export
 */
export const useMiaStore = () => {
  return {
    state: readonly(state),
    getters,
    ...actions,
  };
};

export { state as miaState };
